import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../../core/auth/auth.service';
import { LogoComponent } from '../../../shared/logo/logo.component';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LogoComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="login-shell">
      <!-- Hairlines editoriais -->
      <div class="login-rule login-rule--top"></div>
      <div class="login-rule login-rule--bottom"></div>

      <!-- Card central -->
      <form class="login-card" [formGroup]="form" (ngSubmit)="entrar()">
        <div class="login-head">
          <cs-logo [size]="40" [showWordmark]="true"></cs-logo>
          <h1 class="login-title">Bem-vindo de volta</h1>
          <p class="login-sub">Entre para gerenciar templates de cupom.</p>
        </div>

        @if (sessaoExpirada()) {
          <div class="login-aviso">Sua sessao expirou. Faca login novamente.</div>
        }

        <label class="login-field">
          <span class="login-field__label">Login</span>
          <input
            type="text"
            formControlName="login"
            autocomplete="username"
            placeholder="seu.usuario"
            autofocus />
        </label>

        <label class="login-field">
          <span class="login-field__label">Senha</span>
          <input
            type="password"
            formControlName="password"
            autocomplete="current-password" />
        </label>

        <label class="login-remember">
          <input type="checkbox" checked />
          <span>Manter-me conectado</span>
        </label>

        @if (erro()) {
          <div class="login-erro">{{ erro() }}</div>
        }

        <button class="login-btn" type="submit" [disabled]="form.invalid || loading()">
          {{ loading() ? 'Entrando...' : 'Entrar →' }}
        </button>

        <div class="login-foot">
          Acesso restrito a operadores autorizados.
        </div>
      </form>

      <!-- Meta inferior -->
      <div class="login-meta-left">
        <!-- Marca anterior (mantida comentada): <span>© 2026 SimCare</span> <span class="login-meta-sep"></span> -->
        <a class="login-link">Suporte</a>
        <a class="login-link">Status</a>
      </div>
      <div class="login-meta-right">v2.4.1</div>

      <!-- Rodapé Flua -->
      <footer class="login-footer">
        <div class="login-footer__strip"></div>
        <div class="login-footer__bar">
          <img src="/assets/img/flua-negativo.png" alt="Flua" class="login-footer__logo" />
          <span>Copyright © Flua. All Rights Reserved</span>
        </div>
      </footer>
    </div>
  `,
  styles: [`
    :host { display: block; }

    .login-shell {
      min-height: 100vh;
      display: grid;
      place-items: center;
      background: rgb(var(--color-bg));
      padding: 2rem 1rem;
      font-family: var(--font-sans);
      color: rgb(var(--color-ink));
      position: relative;
    }

    /* Linhas editoriais sutis */
    .login-rule {
      position: absolute;
      left: 32px;
      right: 32px;
      border-top: 1px solid rgb(var(--color-line));
    }
    .login-rule--top    { top: 32px; }
    /* .login-rule--bottom { bottom: 32px; } — anterior; subiu para abrir espaço ao rodapé Flua */
    .login-rule--bottom { bottom: 112px; }

    /* Card */
    .login-card {
      width: 100%;
      max-width: 400px;
      background: rgb(var(--color-paper));
      border: 1px solid rgb(var(--color-line));
      border-radius: var(--radius-lg);
      padding: 40px 36px;
      box-shadow: var(--shadow-md);
      display: flex;
      flex-direction: column;
      gap: 20px;
    }

    .login-head {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 14px;
      text-align: center;
    }

    .login-title {
      margin: 0;
      font-size: 22px;
      font-weight: 600;
      letter-spacing: -0.02em;
      color: rgb(var(--color-ink));
    }

    .login-sub {
      margin: 6px 0 0;
      color: rgb(var(--color-ink-3));
      font-size: 13px;
    }

    /* Campos */
    .login-field {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .login-field__label {
      font-size: 12px;
      font-weight: 500;
      color: rgb(var(--color-ink-2));
      letter-spacing: 0.02em;
    }

    .login-field input {
      height: 40px;
      padding: 0 12px;
      border: 1px solid rgb(var(--color-line-2));
      border-radius: var(--radius);
      background: rgb(var(--color-paper));
      color: rgb(var(--color-ink));
      font-size: 14px;
      font-family: inherit;
      outline: none;
      transition: border-color 120ms, box-shadow 120ms;
    }
    .login-field input:focus {
      border-color: rgb(var(--color-accent));
      box-shadow: 0 0 0 3px rgb(var(--color-accent) / 0.12);
    }

    .login-remember {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 13px;
      color: rgb(var(--color-ink-2));
      cursor: pointer;
      user-select: none;
    }
    .login-remember input {
      accent-color: rgb(var(--color-accent));
    }

    .login-aviso {
      background: rgb(var(--color-warn-bg, 255 243 205));
      color: rgb(var(--color-warn, 133 100 4));
      border: 1px solid rgb(var(--color-warn, 133 100 4) / 0.3);
      border-radius: var(--radius);
      padding: 8px 12px;
      font-size: 13px;
      text-align: center;
    }

    .login-erro {
      background: rgb(var(--color-danger-bg));
      color: rgb(var(--color-danger));
      border: 1px solid rgb(var(--color-danger));
      border-radius: var(--radius);
      padding: 8px 12px;
      font-size: 13px;
    }

    .login-btn {
      height: 42px;
      border: none;
      border-radius: var(--radius);
      background: rgb(var(--color-accent));
      color: #fff;
      font-weight: 600;
      font-size: 14px;
      font-family: inherit;
      cursor: pointer;
      transition: background-color 120ms;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }
    .login-btn:hover:not(:disabled) {
      background: rgb(var(--color-accent-2));
    }
    .login-btn:disabled {
      background: rgb(var(--color-ink-4));
      cursor: not-allowed;
    }

    .login-foot {
      margin-top: 4px;
      padding-top: 18px;
      border-top: 1px solid rgb(var(--color-line));
      font-size: 12px;
      color: rgb(var(--color-ink-3));
      text-align: center;
    }

    /* Meta inferior */
    .login-meta-left,
    .login-meta-right {
      position: absolute;
      /* bottom: 56px; — anterior */
      bottom: 136px;
      font-size: 12px;
      color: rgb(var(--color-ink-3));
    }
    .login-meta-left {
      left: 56px;
      display: flex;
      gap: 20px;
      align-items: center;
    }
    .login-meta-right {
      right: 56px;
      color: rgb(var(--color-ink-4));
    }
    /* Rodapé Flua: faixa de degradê + barra azul */
    .login-footer {
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
    }
    .login-footer__strip {
      height: 8px;
      background: var(--flua-gradient);
    }
    .login-footer__bar {
      display: flex;
      align-items: center;
      /* justify-content: center; — anterior */
      justify-content: space-between;
      gap: 16px;
      padding: 18px 56px;
      background: var(--flua-blue);
      color: #fff;
      font-size: 12px;
    }
    .login-footer__logo {
      height: 28px;
      width: auto;
      display: block;
    }
    .login-meta-sep {
      width: 1px;
      height: 12px;
      background: rgb(var(--color-line-2));
    }
    .login-link {
      color: rgb(var(--color-ink-3));
      text-decoration: none;
      cursor: pointer;
    }
    .login-link:hover {
      color: rgb(var(--color-ink));
    }

    /* Telas pequenas — esconde elementos decorativos */
    @media (max-width: 720px) {
      .login-rule,
      .login-meta-left,
      .login-meta-right {
        display: none;
      }
      .login-card {
        padding: 32px 24px;
      }
    }
  `],
})
export class LoginComponent {
  private readonly fb = inject(FormBuilder);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  protected readonly loading = signal(false);
  protected readonly erro = signal<string | null>(null);
  protected readonly sessaoExpirada = signal(this.route.snapshot.queryParamMap.get('sessaoExpirada') === '1');

  protected readonly form = this.fb.nonNullable.group({
    login: ['', [Validators.required]],
    password: ['', [Validators.required]],
  });

  entrar(): void {
    if (this.form.invalid) return;

    this.loading.set(true);
    this.erro.set(null);

    const { login, password } = this.form.getRawValue();
    this.auth.login(login, password).subscribe({
      next: () => {
        const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') ?? '/inicio';
        this.router.navigateByUrl(returnUrl);
      },
      error: e => {
        const msg = e?.error?.error?.message ?? e?.message ?? 'Falha no login';
        this.erro.set(msg);
        this.loading.set(false);
      },
    });
  }
}
